import React, { useState, useMemo, useEffect } from 'react';
import { AlertCircle, Search, Filter, ArrowUpDown, Check, Mail } from 'lucide-react';
import { dashboardAPI, leadsAPI } from '../services/api';
import PageOptionsButton from './PageOptionsButton';
import { useAuth } from '../contexts/AuthContext';
import { isOwnedBy, normName, resolveBdeIdentity, bdaOptions } from '../utils/bdeOwnership';

const FollowUpTracker = ({ followUps, loading, onMarkCompleted, onExportExcel, onExportPDF, showExport = false, onNavigateToSendEmails, onNavigateToPreSales, onNavigateToPostSales }) => {
  const handleRowClick = async (e, followUp) => {
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input')) return;
    // For a post-sales row, the row's id is `post-sales-<id>` and the lead id
    // lives on lead_id. For a pre-sales row, the row's id IS the lead id.
    const leadId = followUp.is_post_sales ? followUp.lead_id : followUp.id;
    if (!leadId) return;

    // Route to the form that matches the row the user actually clicked: a
    // pre-sales follow-up opens the pre-sales form, a post-sales follow-up opens
    // Post Sales. A lead that has both kinds of rows shows both in the list, and
    // each row opens its own form — clicking a pre-sales row must NOT jump to
    // Post Sales just because the lead also progressed there.
    const goesToPostSales = followUp.is_post_sales;
    const targetCallback = goesToPostSales
      ? (onNavigateToPostSales || onNavigateToPreSales)
      : onNavigateToPreSales;
    if (!targetCallback) return;

    try {
      const res = await leadsAPI.getById(leadId);
      const data = res?.data ?? res;
      if (data) targetCallback(data);
    } catch (err) {
      console.error('Error loading lead for row click:', err);
    }
  };
  // Persist filter state across navigation (e.g., opening pre-sales form and coming back)
  const FILTER_STORAGE_KEY = 'followUpTrackerFilters';
  const loadPersistedFilters = () => {
    try {
      const raw = localStorage.getItem(FILTER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };
  const persisted = loadPersistedFilters();

  const [searchTerm, setSearchTerm] = useState(persisted.searchTerm ?? '');
  const [statusFilter, setStatusFilter] = useState(persisted.statusFilter ?? 'thisweek'); // Default to 'thisweek'
  const [stageFilter, setStageFilter] = useState(persisted.stageFilter ?? 'all'); // 'all', 'pre-sales', 'post-sales'
  // Every BDA sees only their own follow-ups; admins see the whole team and get
  // a name filter built from the real BDE roster (no hard-coded names).
  const { user } = useAuth();
  const [bdeRoster, setBdeRoster] = useState(null);
  const identity = resolveBdeIdentity(user, bdeRoster);
  const restrictToOwn = identity.restrictToOwn;
  const bdeNames = bdaOptions(bdeRoster).map(u => u.name);
  const [nameFilter, setNameFilter] = useState(persisted.nameFilter ?? 'all'); // 'all' or a bde_users.name
  useEffect(() => {
    let cancelled = false;
    dashboardAPI.getBDEUsers()
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        if (cancelled) return;
        setBdeRoster(list);
        // A filter saved before the list was dynamic ("Surabhi", "Hima") no longer
        // matches a full name — fall back to All Names instead of showing nothing.
        const names = bdaOptions(list).map(u => u.name);
        setNameFilter(prev => (prev === 'all' || names.some(n => normName(n) === normName(prev)) ? prev : 'all'));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [sortBy, setSortBy] = useState(persisted.sortBy ?? 'date');
  const [sortOrder, setSortOrder] = useState(persisted.sortOrder ?? 'asc');
  const [sendingEmails, setSendingEmails] = useState(false);
  // Optimistically-marked follow-ups. Hiding the Done button (and the whole row,
  // when the active filter excludes "completed") on click avoids the brief grey
  // "Marking..." flash users were seeing on the second batch of follow-ups —
  // the row disappears immediately and the backend refresh confirms the change.
  const [optimisticallyCompleted, setOptimisticallyCompleted] = useState(() => new Set());
  const isOptimisticallyCompleted = (rowKey) => optimisticallyCompleted.has(rowKey);
  const markOptimistic = (rowKey) => {
    setOptimisticallyCompleted(prev => {
      const next = new Set(prev);
      next.add(rowKey);
      return next;
    });
  };
  const clearOptimistic = (rowKey) => {
    setOptimisticallyCompleted(prev => {
      if (!prev.has(rowKey)) return prev;
      const next = new Set(prev);
      next.delete(rowKey);
      return next;
    });
  };

  // Save filters on change
  React.useEffect(() => {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        searchTerm, statusFilter, stageFilter, nameFilter, sortBy, sortOrder
      }));
    } catch { /* ignore quota errors */ }
  }, [searchTerm, statusFilter, stageFilter, nameFilter, sortBy, sortOrder]);

  // Ensure followUps is always an array
  const safeFollowUps = Array.isArray(followUps) ? followUps : [];

  // Debug: Log the followUps data
  console.log('FollowUpTracker received followUps:', followUps);
  console.log('Safe followUps (array):', safeFollowUps);
  console.log('Post-sales follow-ups count:', safeFollowUps.filter(f => f.is_post_sales).length || 0);
  if (safeFollowUps && safeFollowUps.length > 0) {
    const postSalesFollowUps = safeFollowUps.filter(f => f.is_post_sales);
    console.log('Post-sales follow-ups details:', postSalesFollowUps);
  }

  // Helper function to clean status text (remove "Post Sales" prefix)
  const cleanStatusText = (status) => {
    if (!status) return status;
    return status.replace(/^Post Sales\s+/i, '').trim();
  };

  const getStatusColor = (status) => {
    console.log('getStatusColor called with status:', status);
    switch(status?.toLowerCase()) {
      case 'completed': 
        console.log('Returning completed color for:', status);
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'upcoming': return 'bg-[#e1f0da] text-[#587f2b] border-[#c5e1a5]';
      default: 
        console.log('Returning default color for:', status);
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getUrgencyLevel = (date) => {
    if (!date) return 'none';
    
    // Create today's date in local timezone, set to midnight
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + 
      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
      String(today.getDate()).padStart(2, '0');
    
    // Parse the follow-up date as local date
    const followUpDate = new Date(date + 'T00:00:00');
    const followUpStr = followUpDate.getFullYear() + '-' + 
      String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
      String(followUpDate.getDate()).padStart(2, '0');
    
    // Calculate difference in days
    const todayDate = new Date(todayStr + 'T00:00:00');
    const followUpDateOnly = new Date(followUpStr + 'T00:00:00');
    const diffTime = followUpDateOnly - todayDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'today';
    if (diffDays <= 3) return 'urgent';
    if (diffDays <= 7) return 'soon';
    return 'later';
  };

  // const getUrgencyStyle = (urgency) => {
  //   switch(urgency) {
  //     case 'overdue': return 'border-l-4 border-red-500 bg-red-50';
  //     case 'today': return 'border-l-4 border-orange-500 bg-orange-50';
  //     case 'urgent': return 'border-l-4 border-yellow-500 bg-yellow-50';
  //     case 'soon': return 'border-l-4 border-blue-500 bg-blue-50';
  //     default: return 'border-l-4 border-gray-300 bg-white';
  //   }
  // };

  const getUrgencyBadge = (urgency) => {
    switch(urgency) {
      case 'overdue': return { text: 'OVERDUE', class: 'bg-red-600 text-white' };
      case 'today': return { text: 'TODAY', class: 'bg-orange-600 text-white' };
      case 'urgent': return { text: 'THIS WEEK', class: 'bg-yellow-600 text-white' };
      case 'soon': return { text: 'UPCOMING', class: 'bg-blue-600 text-white' };
      default: return { text: 'LATER', class: 'bg-gray-500 text-white' };
    }
  };

  const isOverdue = (date) => {
    if (!date) return false;
    
    // Create today's date in local timezone
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + 
      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
      String(today.getDate()).padStart(2, '0');
    
    // Parse the follow-up date as local date
    const followUpDate = new Date(date + 'T00:00:00');
    const followUpStr = followUpDate.getFullYear() + '-' + 
      String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
      String(followUpDate.getDate()).padStart(2, '0');
    
    // Compare dates
    const todayDate = new Date(todayStr + 'T00:00:00');
    const followUpDateOnly = new Date(followUpStr + 'T00:00:00');
    
    return followUpDateOnly < todayDate;
  };

  const getDaysUntil = (date) => {
    if (!date) return null;
    
    // Create today's date in local timezone
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + 
      String(today.getMonth() + 1).padStart(2, '0') + '-' + 
      String(today.getDate()).padStart(2, '0');
    
    // Parse the follow-up date as local date
    const followUpDate = new Date(date + 'T00:00:00');
    const followUpStr = followUpDate.getFullYear() + '-' + 
      String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
      String(followUpDate.getDate()).padStart(2, '0');
    
    // Calculate difference in days
    const todayDate = new Date(todayStr + 'T00:00:00');
    const followUpDateOnly = new Date(followUpStr + 'T00:00:00');
    const diffTime = followUpDateOnly - todayDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };

  // Calculate stats
  // const stats = useMemo(() => {
  //   if (!followUps || followUps.length === 0) return {
  //     total: 0, overdue: 0, today: 0, thisWeek: 0, completed: 0
  //   };
    
  //   return {
  //     total: followUps.length,
  //     overdue: followUps.filter(f => 
  //       f.follow_up_date_1 && getUrgencyLevel(f.follow_up_date_1) === 'overdue' && 
  //       f.follow_up_status?.toLowerCase() !== 'completed'
  //     ).length,
  //     today: followUps.filter(f => {
  //       const today = new Date().toDateString();
  //       return f.follow_up_date_1 && new Date(f.follow_up_date_1).toDateString() === today;
  //     }).length,
  //     thisWeek: followUps.filter(f => 
  //       f.follow_up_date_1 && ['urgent', 'soon'].includes(getUrgencyLevel(f.follow_up_date_1))
  //     ).length,
  //     completed: followUps.filter(f => f.follow_up_status?.toLowerCase() === 'completed').length
  //   };
  // }, [followUps]);

  // Filter and sort follow-ups
  const filteredAndSortedFollowUps = useMemo(() => {
    if (!safeFollowUps || safeFollowUps.length === 0) return [];
    
    let filtered = safeFollowUps;

    // No date filtering - we'll use status filter instead

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(f => 
        f.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.follow_up_1_update?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.follow_up_2_update?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.follow_up_3_update?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.follow_up_4_update?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply stage filter (Pre Sales / Post Sales / All)
    if (stageFilter !== 'all') {
      if (stageFilter === 'pre-sales') {
        filtered = filtered.filter(f => !f.is_post_sales);
      } else if (stageFilter === 'post-sales') {
        filtered = filtered.filter(f => f.is_post_sales === true);
      }
    }

    // A BDA only ever sees their own follow-ups. Admins can filter by any BDE.
    if (restrictToOwn) {
      filtered = filtered.filter(f => isOwnedBy(f.bde_name, identity));
    } else if (nameFilter !== 'all') {
      const wanted = normName(nameFilter);
      filtered = filtered.filter(f => normName(f.bde_name) === wanted);
    }

    // Apply status filter
    // Always show all companies, filter individual follow-ups
    // Note: Status filtering is handled in the individual follow-up rendering logic

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let compareValue = 0;
      
      if (sortBy === 'date') {
        const dateA = a.follow_up_date_1 ? new Date(a.follow_up_date_1) : new Date(8640000000000000);
        const dateB = b.follow_up_date_1 ? new Date(b.follow_up_date_1) : new Date(8640000000000000);
        compareValue = dateA - dateB;
      } else if (sortBy === 'company') {
        compareValue = (a.company_name || '').localeCompare(b.company_name || '');
      } else if (sortBy === 'status') {
        compareValue = (a.follow_up_status || '').localeCompare(b.follow_up_status || '');
      }
      
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
    // identity.name / restrictToOwn land once the BDE roster loads, so they must
    // be dependencies — otherwise the list stays unscoped until the next filter change.
  }, [followUps, searchTerm, sortBy, sortOrder, stageFilter, nameFilter, restrictToOwn, identity.name]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleSendEmailsClick = () => {
    if (onNavigateToSendEmails) {
      onNavigateToSendEmails();
    } else {
      handleSendEmailsLegacy();
    }
  };

  const handleSendEmailsLegacy = async () => {
    try {
      setSendingEmails(true);
      const response = await dashboardAPI.triggerEmailWebhook();
      if (response.success) {
        alert(`Emails sent successfully!\n\n${response.message || 'Webhook triggered successfully.'}`);
      } else {
        throw new Error(response.error || 'Failed to send emails');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send emails. Please try again.';
      alert(`Error: ${errorMessage}\n\nCheck browser console (F12) for details.`);
    } finally {
      setSendingEmails(false);
    }
  };

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#679632]"></div>
        <p className="mt-4 text-gray-600">Loading follow-ups...</p>
      </div>
    );
  }

  if (!followUps || followUps.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 text-center">
        <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Follow-ups Scheduled</h3>
        <p className="text-gray-500">Add follow-up dates to your leads to see them here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls Bar */}
      <div className="bg-white rounded shadow p-3 border border-gray-200">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search company or updates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
              />
            </div>
          </div>

          {/* Stage Filter (Pre Sales / Post Sales / All) */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
            >
              <option value="all">All Stages</option>
              <option value="pre-sales">Pre Sales</option>
              <option value="post-sales">Post Sales</option>
            </select>
          </div>

          {/* Name filter — options come from the BDE roster, so every BDA is listed.
              A BDA has nothing to filter: they only see their own follow-ups. */}
          {restrictToOwn ? (
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="px-3 py-2 text-sm text-gray-600">Your follow-ups</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                title="Filter by assigned BDE"
              >
                <option value="all">All Names</option>
                {bdeNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="overdue">Overdue</option>
              <option value="today">Today</option>
              <option value="thisweek">This Week</option>
              <option value="thismonth">This Month</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600">
            {filteredAndSortedFollowUps.length} of {safeFollowUps.length}
          </div>

          {/* Send Emails Button - navigates to Send Emails tab */}
          <button
            onClick={handleSendEmailsClick}
            disabled={sendingEmails && !onNavigateToSendEmails}
            className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            title={onNavigateToSendEmails ? 'Go to Send Emails' : 'Send emails via webhook'}
          >
            <Mail size={16} />
            {onNavigateToSendEmails ? 'Send Emails' : (sendingEmails ? 'Sending...' : 'Send Emails')}
          </button>

          <div className="ml-auto flex-shrink-0">
            <PageOptionsButton onExportExcel={onExportExcel} onExportPDF={onExportPDF} showExport={showExport} />
          </div>
        </div>
      </div>

      {/* Table View */}
      <div className="bg-white rounded shadow border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-base font-semibold text-gray-900">Follow-up Schedule</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {statusFilter !== 'completed' && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Urgency
                    </th>
                  )}
                  <th 
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('company')}
                  >
                    <div className="flex items-center gap-1">
                      Company
                      <ArrowUpDown size={12} className={sortBy === 'company' ? 'text-[#679632]' : 'text-gray-400'} />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('date')}
                  >
                    <div className="flex items-center gap-1">
                      Next Follow-up
                      <ArrowUpDown size={12} className={sortBy === 'date' ? 'text-[#679632]' : 'text-gray-400'} />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Update</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Follow-up Type</th>
                  <th 
                    className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      <ArrowUpDown size={12} className={sortBy === 'status' ? 'text-[#679632]' : 'text-gray-400'} />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedFollowUps.length === 0 ? (
                  <tr>
                    <td colSpan={statusFilter === 'completed' ? 6 : 7} className="px-4 py-8 text-center text-gray-500">
                      No follow-ups match your filters
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedFollowUps.map((followUp, index) => {
                    // Create separate rows for each follow-up record
                    const followUpRecords = [];
                    
                    // The initial sales pitch — its own row, on the lead's
                    // "First Sales Message Sent" date. NOT follow-up 1: this
                    // app's follow-ups are the messages that chase the pitch,
                    // so a lead can have the pitch and all three follow-ups on
                    // the same day (MML does). Filing it as follow-up 1 hid it
                    // behind a real follow-up, which is why it looked missing.
                    if (followUp.initial_pitch_date && !followUp.is_post_sales) {
                      const rowKeyPitch = `${followUp.id}-pitch`;
                      const pitchDone = isOptimisticallyCompleted(rowKeyPitch);
                      // A pitch that has not been sent yet is pending; the row
                      // disappears once it is sent, so "completed" never has one.
                      const showPitch = !(pitchDone && statusFilter !== 'all' && statusFilter !== 'completed')
                        && statusFilter !== 'completed';
                      if (showPitch) {
                        const urgencyPitch = getUrgencyLevel(followUp.initial_pitch_date);
                        const badgePitch = getUrgencyBadge(urgencyPitch);
                        followUpRecords.push(
                          <tr key={`${index}-pitch`} onClick={(e) => handleRowClick(e, followUp)} className="hover:bg-gray-50 cursor-pointer border-l-2 border-green-300">
                            {statusFilter !== 'completed' && (
                              <td className="px-4 py-2 text-sm">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${badgePitch.class}`}>{badgePitch.text}</span>
                              </td>
                            )}
                            <td className="px-4 py-2 text-sm font-medium">{followUp.company_name}</td>
                            <td className="px-4 py-2 text-sm">
                              <div>
                                <div className={urgencyPitch === 'overdue' ? 'text-red-600 font-semibold' : ''}>
                                  {(() => {
                                    const [year, month, day] = String(followUp.initial_pitch_date).split('-').map(Number);
                                    if (!year || !month || !day) return '-';
                                    return new Date(year, month - 1, day).toLocaleDateString('en-US',
                                      { year: 'numeric', month: '2-digit', day: '2-digit' });
                                  })()}
                                </div>
                                <div className="text-xs text-gray-500">{getDaysUntil(followUp.initial_pitch_date)}</div>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">-</td>
                            <td className="px-4 py-2 text-sm text-xs text-gray-600">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                                  Initial Sales Pitch
                                </span>
                                <span className="text-xs text-gray-500">• {followUp.bde_name || 'Unknown BDE'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor('Upcoming')}`}>
                                Upcoming
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {!pitchDone && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!onMarkCompleted) return;
                                    markOptimistic(rowKeyPitch);
                                    try {
                                      // Number 0 — the pitch. Never 1, or this
                                      // would mark a real follow-up done.
                                      await onMarkCompleted(followUp.id, 0, false, followUp.initial_pitch_date);
                                    } catch (err) {
                                      clearOptimistic(rowKeyPitch);
                                    }
                                  }}
                                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                                  title="Mark the initial sales pitch as sent"
                                >
                                  <Check size={14} />
                                  Done
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    }

                    // Follow-up 1
                    if (followUp.follow_up_date_1) {
                      const rowKey1 = `${followUp.id}-1`;
                      const optimisticallyDone1 = isOptimisticallyCompleted(rowKey1);
                      // Check if this individual follow-up should be shown based on status filter
                      const shouldShowFollowUp1 = (() => {
                        // Just-clicked rows disappear immediately on every filter except
                        // "All" and "Completed" (those keep the row visible until refresh).
                        if (optimisticallyDone1 && statusFilter !== 'all' && statusFilter !== 'completed') {
                          return false;
                        }
                        const status = followUp.follow_up_status?.toLowerCase() || '';
                        if (statusFilter === 'all') {
                          return true; // Show all when filter is "all"
                        } else if (statusFilter === 'completed') {
                          return status === 'completed';
                        } else if (statusFilter === 'pending') {
                          return (followUp.follow_up_status?.toLowerCase() === 'pending' || 
                            followUp.follow_up_status?.toLowerCase() === 'upcoming' || !followUp.follow_up_status) &&
                            followUp.follow_up_status?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'overdue') {
                          return followUp.follow_up_date_1 && isOverdue(followUp.follow_up_date_1) && 
                            followUp.follow_up_status?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'today') {
                          if (!followUp.follow_up_date_1) return false;
                          // Use same date comparison approach as getUrgencyLevel
                          const today = new Date();
                          const todayStr = today.getFullYear() + '-' + 
                            String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(today.getDate()).padStart(2, '0');
                          const followUpDate = new Date(followUp.follow_up_date_1 + 'T00:00:00');
                          const followUpStr = followUpDate.getFullYear() + '-' + 
                            String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(followUpDate.getDate()).padStart(2, '0');
                          return followUpStr === todayStr && 
                            followUp.follow_up_status?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thisweek') {
                          if (!followUp.follow_up_date_1) return false;
                          // Include follow-ups from the start of this week to the end of this week
                          // This includes overdue follow-ups that were due this week, today's, and upcoming this week
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          
                          // Get start of week (Sunday = 0, Monday = 1, etc.)
                          const dayOfWeek = today.getDay();
                          const startOfWeek = new Date(today);
                          startOfWeek.setDate(today.getDate() - dayOfWeek); // Go back to Sunday
                          startOfWeek.setHours(0, 0, 0, 0);
                          
                          // Get end of week (7 days from start of week, which is Saturday)
                          const endOfWeek = new Date(startOfWeek);
                          endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
                          endOfWeek.setHours(23, 59, 59, 999);
                          
                          const followUpDate = new Date(followUp.follow_up_date_1 + 'T00:00:00');
                          
                          // Include if follow-up date is within this week (including overdue ones from this week)
                          const isThisWeek = followUpDate >= startOfWeek && followUpDate <= endOfWeek;
                          
                          return isThisWeek && 
                            followUp.follow_up_status?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thismonth') {
                          if (!followUp.follow_up_date_1) return false;
                          // Check if follow-up date is within the current month
                          const today = new Date();
                          const currentYear = today.getFullYear();
                          const currentMonth = today.getMonth();
                          const followUpDate = new Date(followUp.follow_up_date_1 + 'T00:00:00');
                          const followUpYear = followUpDate.getFullYear();
                          const followUpMonth = followUpDate.getMonth();
                          return followUpYear === currentYear && followUpMonth === currentMonth && 
                            followUp.follow_up_status?.toLowerCase() !== 'completed';
                        }
                        return true;
                      })();
                      
                      if (shouldShowFollowUp1) {
                      const urgency1 = getUrgencyLevel(followUp.follow_up_date_1);
                      const urgencyBadge1 = getUrgencyBadge(urgency1);
                      const daysText1 = getDaysUntil(followUp.follow_up_date_1);
                      
                      followUpRecords.push(
                        <tr key={`${index}-1`} onClick={(e) => handleRowClick(e, followUp)} className={`hover:bg-gray-50 cursor-pointer border-l-2 ${followUp.is_post_sales ? 'border-orange-300' : 'border-blue-200'}`}>
                        {statusFilter !== 'completed' && (
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${urgencyBadge1.class}`}>
                              {urgencyBadge1.text}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-2 text-sm font-medium">{followUp.company_name}</td>
                        <td className="px-4 py-2 text-sm">
                            <div>
                              <div className={urgency1 === 'overdue' ? 'text-red-600 font-semibold' : ''}>
                                {(() => {
                                  if (!followUp.follow_up_date_1) return '-';
                                  // Parse date string (YYYY-MM-DD) in local timezone to avoid timezone issues
                                  const [year, month, day] = followUp.follow_up_date_1.split('-').map(Number);
                                  const date = new Date(year, month - 1, day); // month is 0-indexed
                                  return date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                  });
                                })()}
                              </div>
                              <div className="text-xs text-gray-500">{daysText1}</div>
                            </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={followUp.follow_up_1_update}>
                          {followUp.follow_up_1_update || '-'}
                        </td>
                        <td className="px-4 py-2 text-sm text-xs text-gray-600">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs ${followUp.is_post_sales ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                Follow-up 1
                              </span>
                              <span className="text-xs text-gray-500">• {followUp.bde_name || 'Unknown BDE'}</span>
                            </div>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(followUp.follow_up_status)}`}>
                            {cleanStatusText(followUp.follow_up_status) || 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {followUp.follow_up_status?.toLowerCase() !== 'completed' && followUp.follow_up_date_1 && !optimisticallyDone1 && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!onMarkCompleted) return;
                                // Hide button + row immediately; the backend call and refresh run in the background.
                                markOptimistic(rowKey1);
                                try {
                                  const leadId = followUp.is_post_sales ? followUp.lead_id : followUp.id;
                                  await onMarkCompleted(leadId, 1, followUp.is_post_sales, followUp.follow_up_date_1);
                                } catch (err) {
                                  // Roll back the optimistic hide so the user can retry.
                                  clearOptimistic(rowKey1);
                                }
                              }}
                              className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                              title="Mark Follow-up 1 as completed"
                            >
                              <Check size={14} />
                              Done
                            </button>
                          )}
                          </td>
                        </tr>
                      );
                      }
                    }
                    
                    // Follow-up 2
                    if (followUp.follow_up_date_2) {
                      const rowKey2 = `${followUp.id}-2`;
                      const optimisticallyDone2 = isOptimisticallyCompleted(rowKey2);
                      // Check if this individual follow-up should be shown based on status filter
                      const shouldShowFollowUp2 = (() => {
                        if (optimisticallyDone2 && statusFilter !== 'all' && statusFilter !== 'completed') {
                          return false;
                        }
                        const status = followUp.follow_up_status_2?.toLowerCase() || '';
                        if (statusFilter === 'all') {
                          return true; // Show all when filter is "all"
                        } else if (statusFilter === 'completed') {
                          return status === 'completed';
                        } else if (statusFilter === 'pending') {
                          return (followUp.follow_up_status_2?.toLowerCase() === 'pending' || 
                            followUp.follow_up_status_2?.toLowerCase() === 'upcoming' || !followUp.follow_up_status_2) &&
                            followUp.follow_up_status_2?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'overdue') {
                          return followUp.follow_up_date_2 && isOverdue(followUp.follow_up_date_2) && 
                            followUp.follow_up_status_2?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'today') {
                          if (!followUp.follow_up_date_2) return false;
                          // Use same date comparison approach as getUrgencyLevel
                          const today = new Date();
                          const todayStr = today.getFullYear() + '-' + 
                            String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(today.getDate()).padStart(2, '0');
                          const followUpDate = new Date(followUp.follow_up_date_2 + 'T00:00:00');
                          const followUpStr = followUpDate.getFullYear() + '-' + 
                            String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(followUpDate.getDate()).padStart(2, '0');
                          return followUpStr === todayStr && 
                            followUp.follow_up_status_2?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thisweek') {
                          if (!followUp.follow_up_date_2) return false;
                          // Include follow-ups from the start of this week to the end of this week
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dayOfWeek = today.getDay();
                          const startOfWeek = new Date(today);
                          startOfWeek.setDate(today.getDate() - dayOfWeek);
                          startOfWeek.setHours(0, 0, 0, 0);
                          const endOfWeek = new Date(startOfWeek);
                          endOfWeek.setDate(startOfWeek.getDate() + 6);
                          endOfWeek.setHours(23, 59, 59, 999);
                          const followUpDate = new Date(followUp.follow_up_date_2 + 'T00:00:00');
                          const isThisWeek = followUpDate >= startOfWeek && followUpDate <= endOfWeek;
                          return isThisWeek && 
                            followUp.follow_up_status_2?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thismonth') {
                          if (!followUp.follow_up_date_2) return false;
                          // Check if follow-up date is within the current month
                          const today = new Date();
                          const currentYear = today.getFullYear();
                          const currentMonth = today.getMonth();
                          const followUpDate = new Date(followUp.follow_up_date_2 + 'T00:00:00');
                          const followUpYear = followUpDate.getFullYear();
                          const followUpMonth = followUpDate.getMonth();
                          return followUpYear === currentYear && followUpMonth === currentMonth && 
                            followUp.follow_up_status_2?.toLowerCase() !== 'completed';
                        }
                        return true;
                      })();
                      
                      if (shouldShowFollowUp2) {
                      const urgency2 = getUrgencyLevel(followUp.follow_up_date_2);
                      const urgencyBadge2 = getUrgencyBadge(urgency2);
                      const daysText2 = getDaysUntil(followUp.follow_up_date_2);
                      
                      followUpRecords.push(
                        <tr key={`${index}-2`} onClick={(e) => handleRowClick(e, followUp)} className={`hover:bg-gray-50 cursor-pointer border-l-2 ${followUp.is_post_sales ? 'border-orange-300' : 'border-purple-200'}`}>
                          {statusFilter !== 'completed' && (
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${urgencyBadge2.class}`}>
                                {urgencyBadge2.text}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-2 text-sm font-medium">{followUp.company_name}</td>
                          <td className="px-4 py-2 text-sm">
                            <div>
                              <div className={urgency2 === 'overdue' ? 'text-red-600 font-semibold' : ''}>
                                {(() => {
                                  if (!followUp.follow_up_date_2) return '-';
                                  // Parse date string (YYYY-MM-DD) in local timezone to avoid timezone issues
                                  const [year, month, day] = followUp.follow_up_date_2.split('-').map(Number);
                                  const date = new Date(year, month - 1, day); // month is 0-indexed
                                  return date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                  });
                                })()}
                              </div>
                              <div className="text-xs text-gray-500">{daysText2}</div>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={followUp.follow_up_2_update}>
                            {followUp.follow_up_2_update || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-xs text-gray-600">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs ${followUp.is_post_sales ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}`}>
                                Follow-up 2
                              </span>
                              <span className="text-xs text-gray-500">• {followUp.bde_name || 'Unknown BDE'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(followUp.follow_up_status_2)}`}>
                              {cleanStatusText(followUp.follow_up_status_2) || 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {followUp.follow_up_status_2?.toLowerCase() !== 'completed' && followUp.follow_up_date_2 && !optimisticallyDone2 && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!onMarkCompleted) return;
                                  markOptimistic(rowKey2);
                                  try {
                                    const leadId = followUp.is_post_sales ? followUp.lead_id : followUp.id;
                                    await onMarkCompleted(leadId, 2, followUp.is_post_sales);
                                  } catch (err) {
                                    clearOptimistic(rowKey2);
                                  }
                                }}
                                className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                                title="Mark Follow-up 2 as completed"
                              >
                                <Check size={14} />
                                Done
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                      }
                    }
                    
                    // Follow-up 3
                    if (followUp.follow_up_date_3) {
                      const rowKey3 = `${followUp.id}-3`;
                      const optimisticallyDone3 = isOptimisticallyCompleted(rowKey3);
                      // Check if this individual follow-up should be shown based on status filter
                      const shouldShowFollowUp3 = (() => {
                        if (optimisticallyDone3 && statusFilter !== 'all' && statusFilter !== 'completed') {
                          return false;
                        }
                        const status = followUp.follow_up_status_3?.toLowerCase() || '';
                        if (statusFilter === 'all') {
                          return true; // Show all when filter is "all"
                        } else if (statusFilter === 'completed') {
                          return status === 'completed';
                        } else if (statusFilter === 'pending') {
                          return (followUp.follow_up_status_3?.toLowerCase() === 'pending' || 
                            followUp.follow_up_status_3?.toLowerCase() === 'upcoming' || !followUp.follow_up_status_3) &&
                            followUp.follow_up_status_3?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'overdue') {
                          return followUp.follow_up_date_3 && isOverdue(followUp.follow_up_date_3) && 
                            followUp.follow_up_status_3?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'today') {
                          if (!followUp.follow_up_date_3) return false;
                          // Use same date comparison approach as getUrgencyLevel
                          const today = new Date();
                          const todayStr = today.getFullYear() + '-' + 
                            String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(today.getDate()).padStart(2, '0');
                          const followUpDate = new Date(followUp.follow_up_date_3 + 'T00:00:00');
                          const followUpStr = followUpDate.getFullYear() + '-' + 
                            String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(followUpDate.getDate()).padStart(2, '0');
                          return followUpStr === todayStr && 
                            followUp.follow_up_status_3?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thisweek') {
                          if (!followUp.follow_up_date_3) return false;
                          // Include follow-ups from the start of this week to the end of this week
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dayOfWeek = today.getDay();
                          const startOfWeek = new Date(today);
                          startOfWeek.setDate(today.getDate() - dayOfWeek);
                          startOfWeek.setHours(0, 0, 0, 0);
                          const endOfWeek = new Date(startOfWeek);
                          endOfWeek.setDate(startOfWeek.getDate() + 6);
                          endOfWeek.setHours(23, 59, 59, 999);
                          const followUpDate = new Date(followUp.follow_up_date_3 + 'T00:00:00');
                          const isThisWeek = followUpDate >= startOfWeek && followUpDate <= endOfWeek;
                          return isThisWeek && 
                            followUp.follow_up_status_3?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thismonth') {
                          if (!followUp.follow_up_date_3) return false;
                          // Check if follow-up date is within the current month
                          const today = new Date();
                          const currentYear = today.getFullYear();
                          const currentMonth = today.getMonth();
                          const followUpDate = new Date(followUp.follow_up_date_3 + 'T00:00:00');
                          const followUpYear = followUpDate.getFullYear();
                          const followUpMonth = followUpDate.getMonth();
                          return followUpYear === currentYear && followUpMonth === currentMonth && 
                            followUp.follow_up_status_3?.toLowerCase() !== 'completed';
                        }
                        return true;
                      })();
                      
                      if (shouldShowFollowUp3) {
                      const urgency3 = getUrgencyLevel(followUp.follow_up_date_3);
                      const urgencyBadge3 = getUrgencyBadge(urgency3);
                      const daysText3 = getDaysUntil(followUp.follow_up_date_3);
                      
                      followUpRecords.push(
                        <tr key={`${index}-3`} onClick={(e) => handleRowClick(e, followUp)} className={`hover:bg-gray-50 cursor-pointer border-l-2 ${followUp.is_post_sales ? 'border-orange-300' : 'border-green-200'}`}>
                          {statusFilter !== 'completed' && (
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${urgencyBadge3.class}`}>
                                {urgencyBadge3.text}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-2 text-sm font-medium">{followUp.company_name}</td>
                          <td className="px-4 py-2 text-sm">
                            <div>
                              <div className={urgency3 === 'overdue' ? 'text-red-600 font-semibold' : ''}>
                                {(() => {
                                  if (!followUp.follow_up_date_3) return '-';
                                  // Parse date string (YYYY-MM-DD) in local timezone to avoid timezone issues
                                  const [year, month, day] = followUp.follow_up_date_3.split('-').map(Number);
                                  const date = new Date(year, month - 1, day); // month is 0-indexed
                                  return date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit'
                                  });
                                })()}
                              </div>
                              <div className="text-xs text-gray-500">{daysText3}</div>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={followUp.follow_up_3_update}>
                            {followUp.follow_up_3_update || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-xs text-gray-600">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs ${followUp.is_post_sales ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                Follow-up 3
                              </span>
                              <span className="text-xs text-gray-500">• {followUp.bde_name || 'Unknown BDE'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(followUp.follow_up_status_3)}`}>
                              {cleanStatusText(followUp.follow_up_status_3) || 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {followUp.follow_up_status_3?.toLowerCase() !== 'completed' && followUp.follow_up_date_3 && !optimisticallyDone3 && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!onMarkCompleted) return;
                                  markOptimistic(rowKey3);
                                  try {
                                    const leadId = followUp.is_post_sales ? followUp.lead_id : followUp.id;
                                    await onMarkCompleted(leadId, 3, followUp.is_post_sales);
                                  } catch (err) {
                                    clearOptimistic(rowKey3);
                                  }
                                }}
                                className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                                title="Mark Follow-up 3 as completed"
                              >
                                <Check size={14} />
                                Done
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                      }
                    }
                    
                    // Follow-up 4 (for post-sales)
                    if (followUp.follow_up_date_4) {
                      const rowKey4 = `${followUp.id}-4`;
                      const optimisticallyDone4 = isOptimisticallyCompleted(rowKey4);
                      // Check if this individual follow-up should be shown based on status filter
                      const shouldShowFollowUp4 = (() => {
                        if (optimisticallyDone4 && statusFilter !== 'all' && statusFilter !== 'completed') {
                          return false;
                        }
                        const status = followUp.follow_up_status_4?.toLowerCase() || '';
                        if (statusFilter === 'all') {
                          return true; // Show all when filter is "all"
                        } else if (statusFilter === 'completed') {
                          return status === 'completed';
                        } else if (statusFilter === 'pending') {
                          return (followUp.follow_up_status_4?.toLowerCase() === 'pending' || 
                            followUp.follow_up_status_4?.toLowerCase() === 'upcoming' || !followUp.follow_up_status_4) &&
                            followUp.follow_up_status_4?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'overdue') {
                          return followUp.follow_up_date_4 && isOverdue(followUp.follow_up_date_4) && 
                            followUp.follow_up_status_4?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'today') {
                          if (!followUp.follow_up_date_4) return false;
                          const today = new Date();
                          const todayStr = today.getFullYear() + '-' + 
                            String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(today.getDate()).padStart(2, '0');
                          const followUpDate = new Date(followUp.follow_up_date_4 + 'T00:00:00');
                          const followUpStr = followUpDate.getFullYear() + '-' + 
                            String(followUpDate.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(followUpDate.getDate()).padStart(2, '0');
                          return followUpStr === todayStr && 
                            followUp.follow_up_status_4?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thisweek') {
                          if (!followUp.follow_up_date_4) return false;
                          // Include follow-ups from the start of this week to the end of this week
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dayOfWeek = today.getDay();
                          const startOfWeek = new Date(today);
                          startOfWeek.setDate(today.getDate() - dayOfWeek);
                          startOfWeek.setHours(0, 0, 0, 0);
                          const endOfWeek = new Date(startOfWeek);
                          endOfWeek.setDate(startOfWeek.getDate() + 6);
                          endOfWeek.setHours(23, 59, 59, 999);
                          const followUpDate = new Date(followUp.follow_up_date_4 + 'T00:00:00');
                          const isThisWeek = followUpDate >= startOfWeek && followUpDate <= endOfWeek;
                          return isThisWeek && 
                            followUp.follow_up_status_4?.toLowerCase() !== 'completed';
                        } else if (statusFilter === 'thismonth') {
                          if (!followUp.follow_up_date_4) return false;
                          const today = new Date();
                          const currentYear = today.getFullYear();
                          const currentMonth = today.getMonth();
                          const followUpDate = new Date(followUp.follow_up_date_4 + 'T00:00:00');
                          const followUpYear = followUpDate.getFullYear();
                          const followUpMonth = followUpDate.getMonth();
                          return followUpYear === currentYear && followUpMonth === currentMonth && 
                            followUp.follow_up_status_4?.toLowerCase() !== 'completed';
                        }
                        return true;
                      })();
                      
                      if (shouldShowFollowUp4) {
                        const urgency4 = getUrgencyLevel(followUp.follow_up_date_4);
                        const urgencyBadge4 = getUrgencyBadge(urgency4);
                        const daysText4 = getDaysUntil(followUp.follow_up_date_4);
                        
                        followUpRecords.push(
                          <tr key={`${index}-4`} onClick={(e) => handleRowClick(e, followUp)} className={`hover:bg-gray-50 cursor-pointer border-l-2 ${followUp.is_post_sales ? 'border-orange-300' : 'border-blue-200'}`}>
                            {statusFilter !== 'completed' && (
                              <td className="px-4 py-2 text-sm">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${urgencyBadge4.class}`}>
                                  {urgencyBadge4.text}
                                </span>
                              </td>
                            )}
                            <td className="px-4 py-2 text-sm font-medium">{followUp.company_name}</td>
                            <td className="px-4 py-2 text-sm">
                              <div>
                                <div className={urgency4 === 'overdue' ? 'text-red-600 font-semibold' : ''}>
                                  {(() => {
                                    if (!followUp.follow_up_date_4) return '-';
                                    const [year, month, day] = followUp.follow_up_date_4.split('-').map(Number);
                                    const date = new Date(year, month - 1, day);
                                    return date.toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit'
                                    });
                                  })()}
                                </div>
                                <div className="text-xs text-gray-500">{daysText4}</div>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate" title={followUp.follow_up_4_update}>
                              {followUp.follow_up_4_update || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-xs text-gray-600">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs ${followUp.is_post_sales ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                  Follow-up 4
                                </span>
                                <span className="text-xs text-gray-500">• {followUp.bde_name || 'Unknown BDE'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(followUp.follow_up_status_4)}`}>
                                {cleanStatusText(followUp.follow_up_status_4) || 'Pending'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {followUp.follow_up_status_4?.toLowerCase() !== 'completed' && followUp.follow_up_date_4 && !optimisticallyDone4 && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!onMarkCompleted) return;
                                    markOptimistic(rowKey4);
                                    try {
                                      const leadId = followUp.is_post_sales ? followUp.lead_id : followUp.id;
                                      await onMarkCompleted(leadId, 4, followUp.is_post_sales);
                                    } catch (err) {
                                      clearOptimistic(rowKey4);
                                    }
                                  }}
                                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                                  title="Mark Follow-up 4 as completed"
                                >
                                  <Check size={14} />
                                  Done
                                </button>
                              )}
                        </td>
                      </tr>
                    );
                      }
                    }
                    
                    return followUpRecords;
                  }).flat()
                )}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};

export default FollowUpTracker;
